import {
  ALL_TRADES,
  findTradeSubserviceById,
  getTradeById,
} from '../constants/mockData/allTrades';
import {
  SEARCH_TASK_GROUP_ORDER,
  SEARCH_TRADE_IDS,
  SUBSERVICE_SEARCH_SYNONYMS,
  SUBSERVICE_TILE_LABELS,
  TRADE_SEARCH_SYNONYMS,
} from '../constants/serviceSearchCatalog';
import {
  WIZARD_URGENCY_OPTIONS,
  getWizardUrgencyOption,
} from '../constants/wizardUrgencyOptions';
import { WIZARD_ADDITIONAL_DETAILS_FIELD_IDS_KEY } from './serviceWizardFormData';
import { formatServiceLocation } from './serviceLocation';

/** Entry route for the homeowner service search (search bar, trade picker, "Something else"). */
export const SERVICE_SEARCH_PATH = '/find-a-pro';

/** `task` query value for "Not sure / show all pros" (no sub-service filter). */
export const ANY_TASK = 'any';

const TRADE_SHORT_LABELS = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  hvac: 'HVAC',
  landscaping: 'Landscaping',
};

const TRADE_PRO_NOUNS = {
  plumbing: { one: 'plumber', many: 'plumbers' },
  electrical: { one: 'electrician', many: 'electricians' },
  hvac: { one: 'HVAC pro', many: 'HVAC pros' },
  landscaping: { one: 'landscaper', many: 'landscapers' },
};

/** Typeahead needs at least this many characters before suggesting. */
export const MIN_SEARCH_QUERY_LENGTH = 2;

// ---------------------------------------------------------------------------
// Catalog lookups
// ---------------------------------------------------------------------------

/** @param {string | null | undefined} tradeId */
export function isSearchTrade(tradeId) {
  return SEARCH_TRADE_IDS.includes(tradeId);
}

/** Short trade name for tiles and chips (e.g. "Landscaping", not "Landscaping & Lawn Care"). */
export function getSearchTradeLabel(tradeId) {
  return TRADE_SHORT_LABELS[tradeId] || getTradeById(tradeId)?.name || '';
}

/** "1 plumber" / "9 plumbers" */
export function formatTradeProCount(tradeId, count) {
  const nouns = TRADE_PRO_NOUNS[tradeId] || { one: 'pro', many: 'pros' };
  return `${count} ${count === 1 ? nouns.one : nouns.many}`;
}

/**
 * Catalog label for a sub-service within a search trade, or null when the slug is not in that trade.
 * @param {string} tradeId
 * @param {string | null | undefined} subServiceType
 */
export function getSearchTaskLabel(tradeId, subServiceType) {
  if (!subServiceType) return null;
  const trade = isSearchTrade(tradeId) ? getTradeById(tradeId) : null;
  return findTradeSubserviceById(trade, subServiceType)?.label ?? null;
}

/**
 * Task screen groups for a trade (front-end only; slugs unchanged), urgent repairs first where
 * `SEARCH_TASK_GROUP_ORDER` says so. `label` is the short tile text; `fullLabel` is the catalog
 * label shown everywhere else.
 * @param {string} tradeId
 * @returns {Array<{ id: string, name: string, options: Array<{ slug: string, label: string, fullLabel: string }> }>}
 */
export function getSearchTaskGroups(tradeId) {
  if (!isSearchTrade(tradeId)) return [];
  const trade = getTradeById(tradeId);
  const order = SEARCH_TASK_GROUP_ORDER[tradeId] ?? [];
  const rank = (group) => {
    const index = order.indexOf(group.id);
    return index === -1 ? order.length : index;
  };
  return [...(trade?.groups ?? [])]
    .sort((a, b) => rank(a) - rank(b))
    .map((group) => ({
      id: group.id,
      name: group.name,
      options: (group.subservices ?? []).map((sub) => ({
        slug: sub.id,
        label: SUBSERVICE_TILE_LABELS[sub.id] || sub.label,
        fullLabel: sub.label,
      })),
    }));
}

/** Timing step options, in display order. */
export function getSearchTimingOptions() {
  return WIZARD_URGENCY_OPTIONS;
}

/** @param {string | null | undefined} timingId */
export function getSearchTimingLabel(timingId) {
  return getWizardUrgencyOption(timingId)?.searchLabel ?? null;
}

// ---------------------------------------------------------------------------
// Typeahead
// ---------------------------------------------------------------------------

/** Lowercase, strip apostrophes and punctuation, collapse whitespace. */
export function normalizeSearchText(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function scorePhrase(phrase, query, tokens) {
  if (!phrase) return 0;
  if (phrase === query) return 100;
  if (phrase.startsWith(query)) return 90;
  if (` ${phrase}`.includes(` ${query}`)) return 80;
  const words = phrase.split(' ');
  if (tokens.every((token) => words.some((word) => word.startsWith(token)))) {
    return 60;
  }
  if (phrase.includes(query)) return 40;
  return 0;
}

function scoreEntry(entry, query, tokens) {
  let best = scorePhrase(entry.normalizedLabel, query, tokens);
  if (best) best += 5;
  for (const phrase of entry.phrases) {
    best = Math.max(best, scorePhrase(phrase, query, tokens));
  }
  if (!best && query.length >= 3 && entry.description) {
    const words = entry.description.split(' ');
    if (tokens.every((token) => words.some((word) => word.startsWith(token)))) {
      best = 25;
    }
  }
  return best;
}

let searchIndex = null;

/** Lazily built index of the 4 search trades and their catalog sub-services. */
export function getServiceSearchIndex() {
  if (searchIndex) return searchIndex;
  const entries = [];
  SEARCH_TRADE_IDS.forEach((tradeId, tradeOrder) => {
    const trade = ALL_TRADES.find((t) => t.id === tradeId);
    if (!trade) return;
    const tradeLabel = getSearchTradeLabel(tradeId);
    entries.push({
      kind: 'trade',
      key: `trade:${tradeId}`,
      serviceType: tradeId,
      subServiceType: null,
      label: tradeLabel,
      tradeLabel,
      order: tradeOrder * 100,
      phrases: [tradeLabel, ...(TRADE_SEARCH_SYNONYMS[tradeId] ?? [])].map(normalizeSearchText),
      description: '',
    });
    let subOrder = 1;
    (trade.groups ?? []).forEach((group) => {
      (group.subservices ?? []).forEach((sub) => {
        entries.push({
          kind: 'subservice',
          key: `sub:${tradeId}:${sub.id}`,
          serviceType: tradeId,
          subServiceType: sub.id,
          label: sub.label,
          tradeLabel,
          order: tradeOrder * 100 + subOrder,
          phrases: (SUBSERVICE_SEARCH_SYNONYMS[sub.id] ?? []).map(normalizeSearchText),
          description: normalizeSearchText(sub.description),
        });
        subOrder += 1;
      });
    });
  });
  searchIndex = entries.map((entry) => ({
    ...entry,
    normalizedLabel: normalizeSearchText(entry.label),
  }));
  return searchIndex;
}

/**
 * Ranked typeahead suggestions over catalog sub-services (labels + synonyms) and trades.
 * @param {string} rawQuery
 * @param {{ limit?: number }} [options]
 * @returns {Array<{ kind: 'trade' | 'subservice', key: string, serviceType: string, subServiceType: string | null, label: string, tradeLabel: string }>}
 */
export function searchServiceCatalog(rawQuery, { limit = 6 } = {}) {
  const query = normalizeSearchText(rawQuery);
  if (query.length < MIN_SEARCH_QUERY_LENGTH) return [];
  const tokens = query.split(' ').filter(Boolean);

  return getServiceSearchIndex()
    .map((entry) => ({ entry, score: scoreEntry(entry, query, tokens) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.entry.order - b.entry.order)
    .slice(0, limit)
    .map(({ entry }) => ({
      kind: entry.kind,
      key: entry.key,
      serviceType: entry.serviceType,
      subServiceType: entry.subServiceType,
      label: entry.label,
      tradeLabel: entry.tradeLabel,
    }));
}

// ---------------------------------------------------------------------------
// Routes and URL state
// ---------------------------------------------------------------------------

function withQuery(path, params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && String(value).trim() !== '') {
      search.set(key, String(value).trim());
    }
  });
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Search bar entry (optionally keeping typed text). */
export function buildSearchEntryPath({ note } = {}) {
  return withQuery(SERVICE_SEARCH_PATH, { note });
}

/** Trade picker — used when the typed text matched nothing. */
export function buildTradeStepPath({ note } = {}) {
  return withQuery(SERVICE_SEARCH_PATH, { step: 'trade', note });
}

/** "Something else" free-text request routed to the NKY Connect team. */
export function buildSomethingElsePath({ note } = {}) {
  return withQuery(SERVICE_SEARCH_PATH, { step: 'other', note });
}

/** Task (sub-service) step for a trade. `from` tracks the path for the progress indicator. */
export function buildTaskStepPath(tradeId, { note, from } = {}) {
  return withQuery(`/${tradeId}`, { from, note });
}

/** Timing step for a picked task; `task` null = "show all pros". */
export function buildTimingStepPath(tradeId, task, { note, from } = {}) {
  return withQuery(`/${tradeId}`, { task: task || ANY_TASK, from, note });
}

/** Provider results. Omitted `task` = all pros in the trade; omitted `timing` = no availability filter. */
export function buildResultsPath(tradeId, { task, timing, note } = {}) {
  return withQuery(`/${tradeId}/providers`, {
    task: task && task !== ANY_TASK ? task : null,
    timing,
    note,
  });
}

/**
 * Validated search state from the URL for a trade route.
 * @param {string} tradeId
 * @param {URLSearchParams} searchParams
 */
export function readServiceSearchParams(tradeId, searchParams) {
  const taskParam = (searchParams.get('task') || '').trim();
  const task =
    taskParam && taskParam !== ANY_TASK && getSearchTaskLabel(tradeId, taskParam)
      ? taskParam
      : null;
  const timingParam = (searchParams.get('timing') || '').trim();
  const timing = getWizardUrgencyOption(timingParam) ? timingParam : null;
  return {
    /** A task answer exists in the URL (specific slug or "any"). */
    hasTaskAnswer: taskParam === ANY_TASK || Boolean(task),
    task,
    timing,
    note: (searchParams.get('note') || '').trim(),
    from: (searchParams.get('from') || '').trim(),
  };
}

/**
 * Results link for a saved search request (account page "view providers").
 * @param {{ serviceType?: string, subServiceType?: string | null, formData?: { urgency?: string | null } }} doc
 * @returns {string | null}
 */
export function buildResultsPathFromSearchRequest(doc) {
  const tradeId = doc?.serviceType;
  if (!isSearchTrade(tradeId)) return null;
  const task = getSearchTaskLabel(tradeId, doc.subServiceType) ? doc.subServiceType : null;
  const urgency = doc.formData?.urgency;
  const timing = getWizardUrgencyOption(urgency) ? urgency : null;
  return buildResultsPath(tradeId, { task, timing });
}

// ---------------------------------------------------------------------------
// Results filtering
// ---------------------------------------------------------------------------

/**
 * Keep providers whose business `Availability` fits the timing answer.
 * @param {object[]} rows service-api rows with `Availability`
 * @param {string | null | undefined} timingId
 */
export function filterRowsByTiming(rows, timingId) {
  const windows = getWizardUrgencyOption(timingId)?.availabilityWindows;
  if (!windows) return rows;
  return rows.filter((row) =>
    (Array.isArray(row.Availability) ? row.Availability : []).some((slot) =>
      windows.includes(slot),
    ),
  );
}

/** True if the service row lists the given sub-service slug. */
export function rowOffersSubservice(row, subServiceType) {
  if (!subServiceType) return true;
  return (Array.isArray(row?.SubServices) ? row.SubServices : []).some(
    (sub) => sub?.SubServiceType === subServiceType,
  );
}

/**
 * Applies the task then timing answers, relaxing each when it would leave nobody to show.
 * Order follows the API (NKYCPS best match).
 * @param {object[]} rows every provider row for the trade
 * @param {{ task?: string | null, timing?: string | null }} answers
 */
export function selectSearchResultRows(rows, { task, timing }) {
  const all = Array.isArray(rows) ? rows : [];
  const taskRows = task ? all.filter((row) => rowOffersSubservice(row, task)) : all;
  const taskRelaxed = Boolean(task) && taskRows.length === 0 && all.length > 0;
  const base = taskRelaxed ? all : taskRows;

  const timed = filterRowsByTiming(base, timing);
  const timingRelaxed =
    Boolean(getWizardUrgencyOption(timing)?.availabilityWindows) &&
    timed.length === 0 &&
    base.length > 0;

  return {
    rows: timingRelaxed ? base : timed,
    taskRelaxed,
    timingRelaxed,
  };
}

// ---------------------------------------------------------------------------
// Search request payloads (POST /service-wizard-request-api)
// ---------------------------------------------------------------------------

/**
 * Wizard state for a catalog search, created when the homeowner contacts a provider.
 * Shape matches `persistActiveWizardSearchRequest` / the API `ServiceWizardFormData`.
 * @param {{ serviceType: string, subServiceType?: string | null, timing?: string | null, note?: string, city?: string, state?: string }} search
 */
export function buildSearchWizardState({
  serviceType,
  subServiceType = null,
  timing = null,
  note = '',
  city = '',
  state = '',
}) {
  const taskLabel = getSearchTaskLabel(serviceType, subServiceType);
  const tradeLabel = getSearchTradeLabel(serviceType) || serviceType;
  const tradePhrase = tradeLabel === 'HVAC' ? tradeLabel : tradeLabel.toLowerCase();
  const timingOption = getWizardUrgencyOption(timing);

  return {
    serviceType,
    subServiceType: taskLabel ? subServiceType : null,
    formData: {
      urgency: timingOption ? timingOption.id : null,
      photos: [],
      details: String(note || '').trim(),
      city: String(city || '').trim(),
      state,
      additionalDetails: {
        [WIZARD_ADDITIONAL_DETAILS_FIELD_IDS_KEY]: {
          problem: subServiceType || 'not-sure',
          ...(timingOption ? { urgency: timingOption.id } : {}),
        },
      },
    },
    requestSummary: {
      problemSummary: taskLabel || `General ${tradePhrase} help`,
      urgency: timingOption ? timingOption.searchLabel : null,
      location: formatServiceLocation({ city, state }),
    },
  };
}

/**
 * Wizard state for "Something else": free text for the NKY Connect team (custom trades are not searched).
 * Timing and location are added by the waitlist form (`withSearchTiming` / `withServiceLocation`).
 * @param {{ description: string }} input
 */
export function buildSomethingElseWizardState({ description }) {
  const text = String(description || '').trim();
  const summary = text.length > 80 ? `${text.slice(0, 77)}…` : text;
  return {
    serviceType: 'other',
    subServiceType: null,
    formData: {
      urgency: null,
      photos: [],
      details: text,
      city: '',
      state: '',
      additionalDetails: {
        [WIZARD_ADDITIONAL_DETAILS_FIELD_IDS_KEY]: { problem: 'something-else' },
      },
    },
    requestSummary: {
      problemSummary: summary || 'Something else',
      urgency: null,
      location: '',
    },
  };
}

/** Sets the timing answer on a wizard state (form data, field ids and readable summary). */
export function withSearchTiming(wizardState, timingId) {
  const timingOption = getWizardUrgencyOption(timingId);
  if (!wizardState || !timingOption) return wizardState;
  const additionalDetails = wizardState.formData.additionalDetails || {};
  return {
    ...wizardState,
    formData: {
      ...wizardState.formData,
      urgency: timingOption.id,
      additionalDetails: {
        ...additionalDetails,
        [WIZARD_ADDITIONAL_DETAILS_FIELD_IDS_KEY]: {
          ...additionalDetails[WIZARD_ADDITIONAL_DETAILS_FIELD_IDS_KEY],
          urgency: timingOption.id,
        },
      },
    },
    requestSummary: { ...wizardState.requestSummary, urgency: timingOption.searchLabel },
  };
}
